import { Box, Flex, Paper, Stack } from "@mantine/core";
import { PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { usePermissionsContext } from "#app/PermissionsProvider";
import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { shadows } from "#app/theme/v2/shadows";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { STEP_HEIGHT } from "#components/atoms/Step/Step";
import { Text } from "#components/atoms/Typographies/Text";
import { Title } from "#components/atoms/Typographies/Title";
import { Stepper } from "#components/molecules/Stepper/Stepper";
import { useCompleteOnboarding } from "#modules/company/view/useCompleteOnboarding";
import { useUser } from "#modules/users/view/useUser";
import { Icon } from "#shared/icons/Icon";
import { ROUTES } from "#shared/routes";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";

const SIZES = {
  sm: {
    height: 416,
    width: 960,
  },
  md: {
    height: 720,
    width: 960,
  },
  lg: {
    height: 984,
    width: 1312,
  },
} as const;

type OnboardingV2ShellSize = keyof typeof SIZES;

type OnboardingV2ShellProps = PropsWithChildren<{
  size?: OnboardingV2ShellSize;
}>;

const MIN_PADDING_TOP_AND_BOTTOM = 48 * 2;

const OnboardingV2ShellRoot = ({ children, size = "md" }: OnboardingV2ShellProps) => {
  const { width, height } = SIZES[size];

  return (
    <Flex
      justify="center"
      align="center"
      h="100vh"
      style={{
        backgroundColor: colors.bg.elevation.secondary,
        overflow: "hidden",
      }}
    >
      <Paper
        radius={radius.xxxl}
        w="100%"
        p={spacing[8]}
        style={{
          minWidth: "min(calc(100% - " + MIN_PADDING_TOP_AND_BOTTOM + "px), " + width + "px)",
          maxWidth: "min(calc(100% - " + MIN_PADDING_TOP_AND_BOTTOM + "px), " + width + "px)",
          minHeight: "min(calc(100% - " + MIN_PADDING_TOP_AND_BOTTOM + "px), " + height + "px)",
          maxHeight: "min(calc(100% - " + MIN_PADDING_TOP_AND_BOTTOM + "px), " + height + "px)",
          display: "flex",
          flexDirection: "column",
          gap: spacing[8],
          overflow: "hidden",
          boxShadow: shadows.elevation3,
        }}
      >
        {children}
      </Paper>
    </Flex>
  );
};

type OnboardingV2ShellHeaderProps = {
  onBack: () => void;
  step: number;
  total: number;
  title: string;
  subtitle?: string;
};

const OnboardingV2ShellHeader = ({
  onBack,
  step,
  total,
  title,
  subtitle,
}: OnboardingV2ShellHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { user } = useUser();
  const { webInterfaceV2 } = usePermissionsContext();
  const { completeOnboarding } = useCompleteOnboarding({
    onSuccess: () => {
      analytics.capture(AnalyticsEvent.OnboardingSavedAndExited, {
        companyId: user?.companyId,
        userId: user?.id,
        onboardingVersion: "v2",
        stepPosition: step,
      });
    },
  });

  const handleSkip = () => {
    void completeOnboarding();
    if (webInterfaceV2) {
      navigate(ROUTES.v2);
    } else {
      navigate(ROUTES.partner);
    }
  };

  return (
    <>
      <div style={{ position: "absolute", right: spacing[6], top: spacing[6] }}>
        <Button variant="tertiary" onClick={handleSkip}>
          {t("onboardingV2.skip")}
        </Button>
      </div>
      <Stack gap={spacing[4]} align="center" style={{ position: "relative", flexShrink: 0 }}>
        <div style={{ position: "absolute", left: 0, top: 0 }}>
          <IconButton
            variant="secondary"
            size="lg"
            aria-label={t("onboardingV2.back")}
            onClick={onBack}
          >
            <Icon name="arrowLeft" size={16} />
          </IconButton>
        </div>
        <Stack gap={spacing[4]} align="center" maw={560}>
          <Text type="medium" color={colors.text.secondaryDefault} textAlign="center">
            {t("onboardingV2.stepCounter", { step, total })}
          </Text>
          <Title size="medium" style={{ textAlign: "center" }}>
            {title}
          </Title>
          {subtitle ? (
            <Text color={colors.text.secondaryDefault} textAlign="center">
              {subtitle}
            </Text>
          ) : null}
        </Stack>
      </Stack>
    </>
  );
};

const OnboardingV2ShellContent = ({
  children,
  overflow = "auto",
}: PropsWithChildren<{ overflow?: "auto" | "hidden" }>) => (
  <Box
    style={{
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow,
    }}
  >
    {children}
  </Box>
);

type OnboardingV2ShellFooterProps = {
  buttonLabel: string;
  onContinue: () => void;
  disabled?: boolean;
  loading?: boolean;
  errorMessage?: string;
  stepper?: { total: number; current: number };
  leftAction?: ReactNode;
};

const OnboardingV2ShellFooter = ({
  buttonLabel,
  onContinue,
  disabled,
  loading,
  errorMessage,
  stepper,
  leftAction,
}: OnboardingV2ShellFooterProps) => (
  <Stack gap={0} align="center" style={{ position: "relative", flexShrink: 0 }}>
    {leftAction ? <div style={{ position: "absolute", left: 0, top: 0 }}>{leftAction}</div> : null}
    <Button size="xl" w={320} disabled={disabled} loading={loading} onClick={onContinue}>
      {buttonLabel}
    </Button>
    <Flex
      align="center"
      gap={spacing[1]}
      style={{ marginTop: spacing[3], visibility: errorMessage ? "visible" : "hidden" }}
    >
      <Icon name="alertTriangle" size={16} color={colors.icon.errorDefault} />
      <Text size="small" color={colors.text.errorDefault}>
        {errorMessage}
      </Text>
    </Flex>
    <Box style={{ marginTop: spacing[4] }}>
      {stepper ? (
        <Stepper total={stepper.total} current={stepper.current} />
      ) : (
        <Box h={STEP_HEIGHT} />
      )}
    </Box>
  </Stack>
);

export const OnboardingV2Shell = Object.assign(OnboardingV2ShellRoot, {
  Header: OnboardingV2ShellHeader,
  Content: OnboardingV2ShellContent,
  Footer: OnboardingV2ShellFooter,
});
